import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/pages/dashboard/components/DashboardLayout";
import { supabase } from "@/lib/supabase";
import { useAuthGuard } from "@/hooks/useAuthGuard";

const PIPELINE = ["new", "contacted", "qualified", "appointment", "closed"] as const;
type PipelineStatus = typeof PIPELINE[number];

const pipelineColors: Record<PipelineStatus, string> = {
  new: "bg-gray-100 text-gray-600",
  contacted: "bg-amber-100 text-amber-700",
  qualified: "bg-green-100 text-green-700",
  appointment: "bg-green-200 text-green-800",
  closed: "bg-gray-900 text-white",
};

interface PurchasedLead {
  id: string;
  state: string;
  city: string;
  zip: string;
  bill_range: string;
  solar_score: number;
  lead_score: number;
  timeline: string;
  price: number;
  name: string;
  phone: string;
  address: string;
  notes: string;
  pipeline_status: PipelineStatus;
  purchased_at: string;
  amount_paid: number;
  purchase_id: string;
}

export default function PurchasedLeadsPage() {
  const { loading: authLoading } = useAuthGuard();
  const [leads, setLeads] = useState<PurchasedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<PurchasedLead | null>(null);
  const [noteInput, setNoteInput] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [searchParams] = useSearchParams();
  const justPurchased = searchParams.get("success") === "true";

  const fetchPurchasedLeads = useCallback(async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }

    const { data: purchases, error } = await supabase
      .from("lead_purchases")
      .select(`id, amount, created_at, leads (id, state, city, zip, bill_range, solar_score, lead_score, timeline, price, name, phone, address, notes)`)
      .eq("installer_id", user.id)
      .eq("status", "completed")
      .order("created_at", { ascending: false });

    if (error || !purchases) { setLoading(false); return; }

    const leadIds = purchases.map((p: Record<string, unknown>) => (p.leads as Record<string, unknown>)?.id).filter(Boolean);
    const { data: statuses } = await supabase
      .from("lead_status_updates")
      .select("lead_id, status, updated_at")
      .in("lead_id", leadIds)
      .eq("installer_id", user.id)
      .order("updated_at", { ascending: false });

    const latestStatus: Record<string, PipelineStatus> = {};
    (statuses || []).forEach((s: Record<string, unknown>) => {
      if (!latestStatus[s.lead_id as string]) {
        latestStatus[s.lead_id as string] = s.status as PipelineStatus;
      }
    });

    const mapped: PurchasedLead[] = purchases.map((p: Record<string, unknown>) => {
      const lead = p.leads as Record<string, unknown>;
      return {
        id: lead?.id as string,
        state: lead?.state as string,
        city: lead?.city as string,
        zip: lead?.zip as string,
        bill_range: lead?.bill_range as string,
        solar_score: lead?.solar_score as number,
        lead_score: lead?.lead_score as number,
        timeline: lead?.timeline as string,
        price: lead?.price as number,
        name: (lead?.name as string) || "—",
        phone: (lead?.phone as string) || "—",
        address: (lead?.address as string) || "—",
        notes: (lead?.notes as string) || "",
        pipeline_status: latestStatus[lead?.id as string] || "new",
        purchased_at: new Date(p.created_at as string).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
        amount_paid: p.amount as number,
        purchase_id: p.id as string,
      };
    });

    setLeads(mapped);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!authLoading) fetchPurchasedLeads();
  }, [fetchPurchasedLeads, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <i className="ri-loader-4-line animate-spin text-3xl text-[#16a34a]"></i>
      </div>
    );
  }

  const updateStatus = async (leadId: string, status: PipelineStatus) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from("lead_status_updates").insert({ lead_id: leadId, installer_id: user.id, status });
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, pipeline_status: status } : l));
    if (selectedLead?.id === leadId) setSelectedLead((prev) => prev ? { ...prev, pipeline_status: status } : null);
  };

  const addNote = async (leadId: string) => {
    if (!noteInput.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setSavingNote(true);
    await supabase.from("lead_notes").insert({ lead_id: leadId, installer_id: user.id, note: noteInput.trim() });
    const newNote = noteInput.trim();
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, notes: l.notes ? l.notes + "\n\n" + newNote : newNote } : l));
    if (selectedLead?.id === leadId) setSelectedLead((prev) => prev ? { ...prev, notes: prev.notes ? prev.notes + "\n\n" + newNote : newNote } : null);
    setNoteInput("");
    setSavingNote(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchased Leads</h1>
          <p className="text-gray-500 text-sm mt-1">
            {loading ? "Loading..." : `${leads.length} leads purchased · Full contact details unlocked`}
          </p>
        </div>

        {justPurchased && (
          <div className="bg-green-50 border border-green-300 rounded-xl px-5 py-4 flex items-center gap-3 text-green-800 text-sm font-medium">
            <i className="ri-check-double-line text-lg"></i>
            Lead purchased successfully! Full contact details are now available below.
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 animate-pulse">
                <div className="h-4 bg-gray-100 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-100 rounded w-1/3 mb-4"></div>
                <div className="h-8 bg-gray-100 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-shopping-bag-line text-4xl mb-3 block"></i>
            <p className="font-medium">No purchased leads yet</p>
            <p className="text-sm mt-1">Head to the marketplace to buy your first lead</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {leads.map((lead) => (
              <div
                key={lead.id}
                className={`bg-white border rounded-2xl p-5 cursor-pointer transition-all hover:border-green-200 ${selectedLead?.id === lead.id ? "border-green-400 ring-2 ring-green-100" : "border-gray-100"}`}
                onClick={() => setSelectedLead(lead)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-gray-900">{lead.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{lead.city}, {lead.state} · Purchased {lead.purchased_at}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${pipelineColors[lead.pipeline_status]}`}>
                    {lead.pipeline_status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                  <span className="flex items-center gap-1"><i className="ri-phone-line text-[#16a34a]"></i>{lead.phone}</span>
                  <span className="flex items-center gap-1"><i className="ri-money-dollar-circle-line text-[#16a34a]"></i>{lead.bill_range}/mo</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {PIPELINE.map((s) => (
                    <button
                      key={s}
                      onClick={(e) => { e.stopPropagation(); updateStatus(lead.id, s); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition cursor-pointer whitespace-nowrap capitalize ${lead.pipeline_status === s ? pipelineColors[s] + " border-transparent" : "border-gray-200 text-gray-500 hover:border-gray-400"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedLead && (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Lead Details — {selectedLead.name}</h2>
              <button onClick={() => setSelectedLead(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {[
                { label: "Phone", value: selectedLead.phone, icon: "ri-phone-line" },
                { label: "Address", value: selectedLead.address, icon: "ri-map-pin-line" },
                { label: "Monthly Bill", value: selectedLead.bill_range, icon: "ri-money-dollar-circle-line" },
                { label: "Timeline", value: selectedLead.timeline, icon: "ri-time-line" },
                { label: "Lead Score", value: String(selectedLead.lead_score), icon: "ri-user-star-line" },
                { label: "Amount Paid", value: `$${selectedLead.amount_paid}`, icon: "ri-secure-payment-line" },
              ].map((item) => (
                <div key={item.label} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-gray-400 text-xs mb-1 flex items-center gap-1">
                    <i className={item.icon}></i>{item.label}
                  </p>
                  <p className="font-semibold text-gray-900">{item.value}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Notes</p>
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-line min-h-[80px] mb-3">
                {selectedLead.notes || "No notes yet."}
              </div>
              <div className="flex gap-2">
                <input
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addNote(selectedLead.id)}
                  placeholder="Add a note..."
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  onClick={() => addNote(selectedLead.id)}
                  disabled={savingNote}
                  className="px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {savingNote ? <i className="ri-loader-4-line animate-spin"></i> : "Add Note"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
